import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, Modal, InputBase, Switch, IconButton, Tooltip } from "@mui/material";
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LaunchScreenTable } from "../../sub_components/launch_screen_table";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CustomAxios from "../../utils/CustomAxios";

export default function LaunchScreenSettings() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [launchScreens, setLaunchScreens] = useState([]);
  const [launchScreenBanners, setLaunchScreenBanners] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    upload_type: "video",
    video_file: null,
    video_file_name: "",
    json_file: null,
    json_file_name: "",
    is_active: true,
    banners: [
      {
        heading: "",
        description: "",
        background_color: "",
        image: [],
        imageFiles: [],
        imagePreviews: [],
        is_active: true,
      },
    ],
  });

  const resetForm = () => {
    setForm({
      title: "",
      upload_type: "video",
      video_file: null,
      video_file_name: "",
      json_file: null,
      json_file_name: "",
      is_active: true,
      banners: [
        { heading: "", description: "", background_color: "", content_color: "", image: [], imageFiles: [], imagePreviews: [], is_active: true },
      ],
    });
  };

  const fetchLaunchScreenBanners = async () => {
    try {
      const res = await CustomAxios.get('launch_screen_banners/');
      const banners = Array.isArray(res?.data) ? res.data : [];
      setLaunchScreenBanners(banners);
      return banners;
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch launch screen banners' });
      return [];
    }
  };

  const fetchLaunchScreens = async () => {
    setLoading(true);
    try {
      // Fetch both launch screens and banners in parallel
      const [lsRes, banners] = await Promise.all([
        CustomAxios.get('launch_screens/'),
        fetchLaunchScreenBanners(),
      ]);

      const screens = Array.isArray(lsRes?.data) ? lsRes.data : [];

      // If screens already include banner objects, keep as is.
      // If they include banner IDs, enrich from fetched banners.
      const bannerById = new Map((banners || []).map((b) => [b.id, b]));
      const enriched = screens.map((s) => {
        if (Array.isArray(s.banners) && s.banners.length > 0) {
          const areObjects = typeof s.banners[0] === 'object' && s.banners[0] !== null;
          if (areObjects) return s;
          const resolved = s.banners
            .map((id) => bannerById.get(id))
            .filter(Boolean);
          return { ...s, banners: resolved };
        }
        return { ...s, banners: [] };
      });

      setLaunchScreens(enriched);
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch launch screens" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLaunchScreens();
  }, []);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => { setModalOpen(false); setEditId(null); resetForm(); };

  const openEdit = (item) => {
    console.log('item-----------------    ',item);
    setEditId(item.id);
    const videoUrl = item.video_file || item.video_link || item.video || null;
    const jsonUrl = item.json_file_link || item.json_file || null;
    const uploadType = jsonUrl ? "json" : "video";
    
    setForm({
      title: item.title || "",
      upload_type: uploadType,
      // Support multiple possible backend field names for existing video URL
      video_file: videoUrl,
      video_file_name: videoUrl ? String(videoUrl).split('/').pop() || "" : "",
      json_file: jsonUrl,
      json_file_name: jsonUrl ? String(jsonUrl).split('/').pop() || "" : "",
      is_active: !!item.is_active,
      banners: (item.banners || []).map((b) => ({
        id: b.id,
        heading: b.heading || "",
        description: b.description || "",
        background_color: b.background_color || "",
        content_color: b.content_color || "",
        image: Array.isArray(b.image) ? b.image : [],
        imageFiles: [],
        // Prefill previews with existing image URLs so they show on edit
        imagePreviews: Array.isArray(b.image) ? b.image : [],
        is_active: !!b.is_active,
      })),
    });
    setModalOpen(true);
  };

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateSwitch = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const updateBannerField = (index, e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev };
      next.banners[index] = { ...next.banners[index], [name]: value };
      return next;
    });
  };

  const updateBannerSwitch = (index, e) => {
    const { name, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev };
      next.banners[index] = { ...next.banners[index], [name]: checked };
      return next;
    });
  };

  const updateBannerImages = (index, e) => {
    const { value } = e.target;
    const list = value
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setForm((prev) => {
      const next = { ...prev };
      next.banners[index] = { ...next.banners[index], image: list };
      return next;
    });
  };

  const handleBannerFileChange = (index, e) => {
    const files = Array.from(e.target.files || []);
    const previews = files.map((file) => URL.createObjectURL(file));
    setForm((prev) => {
      const next = { ...prev };
      const current = next.banners[index] || {};
      next.banners[index] = {
        ...current,
        imageFiles: [...(current.imageFiles || []), ...files],
        imagePreviews: [...(current.imagePreviews || []), ...previews],
      };
      return next;
    });
  };

  const handleRemoveBannerImage = (index, imgIdx) => {
    setForm((prev) => {
      const next = { ...prev };
      const current = next.banners[index];
      if (!current) return prev;
      const newFiles = (current.imageFiles || []).filter((_, i) => i !== imgIdx);
      const newPreviews = (current.imagePreviews || []).filter((_, i) => i !== imgIdx);
      next.banners[index] = { ...current, imageFiles: newFiles, imagePreviews: newPreviews };
      return next;
    });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, video_file: file, video_file_name: file ? file.name : "" }));
  };

  const handleJsonChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, json_file: file, json_file_name: file ? file.name : "" }));
  };

  const handleRemoveVideo = () => {
    setForm((prev) => ({ ...prev, video_file: null, video_file_name: "" }));
  };

  const handleRemoveJson = () => {
    setForm((prev) => ({ ...prev, json_file: null, json_file_name: "" }));
  };

  const handleUploadTypeChange = (e) => {
    const newType = e.target.value;
    setForm((prev) => ({
      ...prev,
      upload_type: newType,
      // Clear the opposite file type when switching
      video_file: newType === "json" ? null : prev.video_file,
      video_file_name: newType === "json" ? "" : prev.video_file_name,
      json_file: newType === "video" ? null : prev.json_file,
      json_file_name: newType === "video" ? "" : prev.json_file_name,
    }));
  };

  const addBanner = () => {
    setForm((prev) => ({
      ...prev,
      banners: [
        ...prev.banners,
        { heading: "", description: "", background_color: "", image: [], imageFiles: [], imagePreviews: [], is_active: true },
      ],
    }));
  };

  const removeBanner = async (index) => {
    const banner = form.banners?.[index];
    if (!banner) return;
    
    setForm((prev) => ({
      ...prev,
      banners: prev.banners.filter((_, i) => i !== index),
    }));
  };

  const saveLaunchScreen = async () => {
    // Yup validation - all fields mandatory
    try {
      const bannerSchema = Yup.object().shape({
        heading: Yup.string().trim().required('Banner heading is required'),
        description: Yup.string().trim().required('Banner description is required'),
        background_color: Yup.string().trim().required('Banner background color is required'),
        is_active: Yup.boolean().required('Banner active status is required'),
        image: Yup.array().of(Yup.mixed()).nullable(),
        imageFiles: Yup.array().of(Yup.mixed()).nullable(),
      }).test('has-image', 'Banner must include at least one image', (value) => {
        const filesLen = Array.isArray(value?.imageFiles) ? value.imageFiles.length : 0;
        const urlsLen = Array.isArray(value?.image) ? value.image.length : 0;
        return filesLen + urlsLen > 0;
      });

      const schema = Yup.object().shape({
        title: Yup.string().trim().required('Title is required'),
        is_active: Yup.boolean().required('Active status is required'),
        upload_type: Yup.string().oneOf(['video', 'json']).required('Upload type is required'),
        video_file: Yup.mixed().when('upload_type', {
          is: 'video',
          then: (schema) => schema.test('video-required', 'Video is required', (val) => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') return val.trim().length > 0;
            if (typeof File !== 'undefined' && val instanceof File) return true;
            return !!val;
          }),
          otherwise: (schema) => schema.nullable(),
        }),
        json_file: Yup.mixed().when('upload_type', {
          is: 'json',
          then: (schema) => schema.test('json-required', 'JSON file is required', (val) => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') return val.trim().length > 0;
            if (typeof File !== 'undefined' && val instanceof File) return true;
            return !!val;
          }),
          otherwise: (schema) => schema.nullable(),
        }),
        banners: Yup.array().of(bannerSchema).min(1, 'At least one banner is required').required('Banners are required'),
      });

      await schema.validate(form, { abortEarly: false });
    } catch (validationErrors) {
      if (validationErrors && validationErrors.name === 'ValidationError') {
        const firstError = Array.isArray(validationErrors.errors) && validationErrors.errors.length > 0
          ? validationErrors.errors[0]
          : 'Please fix validation errors';
        toast.current?.show({ severity: 'warn', summary: 'Validation', detail: firstError });
        return;
      }
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please fix validation errors' });
      return;
    }
    setSaving(true);
    try {
      // 1) Create/retain banners
      const bannerIds = [];
      const selectedFranchiseId = localStorage.getItem('selected_franchise_id');
      for (const banner of form.banners) {
        if (banner.id && (banner.imageFiles?.length || 0) === 0 && (!banner.heading && !banner.description && !banner.background_color)) {
          bannerIds.push(banner.id);
          continue;
        }
        if (!banner.heading && !banner.description && !banner.background_color && !banner.content_color && (banner.image?.length || 0) === 0 && (banner.imageFiles?.length || 0) === 0) {
          continue;
        }

        const data = new FormData();
        data.append('heading', banner.heading || '');
        data.append('description', banner.description || '');
        data.append('background_color', banner.background_color || '');
        if (banner.content_color) {
          data.append('content_color', banner.content_color || '');
        }
        if (selectedFranchiseId) {
          data.append('franchise', String(parseInt(selectedFranchiseId)));
        }
        data.append('is_active', banner.is_active ? 'true' : 'false');
        if ((banner.imageFiles?.length || 0) > 0) {
          (banner.imageFiles || []).forEach((file) => {
            if (file instanceof File) {
              data.append('image', file);
            }
          });
        } else if (Array.isArray(banner.image) && banner.image.length > 0) {
          // Only send URLs if present; omit entirely when empty so backend can use default
          data.append('image', JSON.stringify(banner.image));
        }

        if (banner.id) {
          await CustomAxios.patch(`launch_screen_banners/${banner.id}/`, data);
          bannerIds.push(banner.id);
        } else {
          const res = await CustomAxios.post('launch_screen_banners/', data);
          if (res?.data?.id) bannerIds.push(res.data.id);
        }
      }

      // 2) Create or Update launch screen
      const lsForm = new FormData();
      lsForm.append('title', form.title);
      lsForm.append('is_active', form.is_active ? 'true' : 'false');
      
      if (form.upload_type === 'video' && form.video_file) {
        if (form.video_file instanceof File) {
          lsForm.append('video_file', form.video_file);
        }
      } else if (form.upload_type === 'json' && form.json_file) {
        if (form.json_file instanceof File) {
          lsForm.append('json_file', form.json_file);
        }
      }
      
      bannerIds.forEach((id) => lsForm.append('banners', id));
      if (editId) {
        await CustomAxios.patch(`launch_screens/${editId}/`, lsForm);
      } else {
        await CustomAxios.post('launch_screens/', lsForm);
      }

      toast.current?.show({ severity: "success", summary: "Success", detail: "Launch screen saved" });
      handleClose();
      await fetchLaunchScreens();
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to save launch screen" });
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%", padding: "30px" }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}>Settings</Typography>
        <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>Launch Screen</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Box />
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Button variant="contained" color="primary" sx={{ textTransform: "none" }} onClick={handleOpen}>
            <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
              Add Items
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", background: "#FFF", padding: "20px" }}>
        <LaunchScreenTable
          items={launchScreens}
          onEdit={openEdit}
          onDelete={(item) => {
            if (!item?.id) return;
            confirmDialog({
              message: (
                <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                  <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                    Do you want to Delete this Launch Screen?
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: '#6B7280' }}>
                    {item.title || ''}
                  </Typography>
                </Box>
              ),
              header: 'Delete Confirmation',
              defaultFocus: 'reject',
              acceptClassName: 'p-button-danger',
              accept: async () => {
                try {
                  // Delete associated banners first (supports arrays of objects or IDs)
                  const bannerIds = Array.isArray(item.banners)
                    ? item.banners
                        .map((b) => (typeof b === 'object' && b !== null ? b.id : b))
                        .filter((id) => typeof id === 'number' || typeof id === 'string')
                    : [];

                  if (bannerIds.length > 0) {
                    await Promise.allSettled(
                      bannerIds.map((bid) => CustomAxios.delete(`launch_screen_banners/${bid}/`))
                    );
                  }

                  // Now delete the launch screen itself
                  await CustomAxios.delete(`launch_screens/${item.id}/`);
                  toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Launch screen deleted' });
                  await fetchLaunchScreens();
                } catch (error) {
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete launch screen' });
                  console.error(error);
                }
              },
            });
          }}
        />
      </Box>

      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ zIndex: 1000 }}
      >
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', maxWidth: "900px", height: "82vh", backgroundColor: 'white' }}>
          <Box sx={{ height: "46px important", padding: "12px 24px", backgroundColor: "#EEEEEE", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "14", fontWeight: "500", lineHeight: "22px" }}>Add Launch Screen</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2">Active</Typography>
              <Switch checked={form.is_active} name="is_active" onChange={updateSwitch} />
            </Box>
          </Box>
          <Box>
            <Box sx={{ padding: "24px", display: "flex", gap: "16px", flexDirection: "column", height: "calc(80vh - 46px - 60px)", overflowY: "auto" }}>
              <Box sx={{ width: "100%", display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Title<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                  <InputBase name="title" value={form.title} onChange={updateField} placeholder="Enter title"
                    sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Upload Type<span style={{ color: "red", marginLeft: "5px" }}>*</span></Typography>
                  <select 
                    name="upload_type" 
                    value={form.upload_type} 
                    onChange={handleUploadTypeChange}
                    style={{ 
                      backgroundColor: "white", 
                      height: "32px", 
                      width: "100%", 
                      borderRadius: "2px", 
                      border: "1px solid #ccc", 
                      padding: "5px 12px", 
                      fontSize: "14px", 
                      fontWeight: "400", 
                      lineHeight: "22px", 
                      color: "black" 
                    }}
                  >
                    <option value="video">Video</option>
                    <option value="json">JSON</option>
                  </select>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                  <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>
                    {form.upload_type === 'video' ? 'Video' : 'JSON File'}<span style={{ color: "red", marginLeft: "5px" }}>*</span>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {form.upload_type === 'video' ? (
                      <Box sx={{ minWidth: 220, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {!form.video_file ? (
                          <Box sx={{ position: 'relative', border: '1px dashed', borderColor: 'secondary.border', borderRadius: '6px', px: 1, py: 0.5 }}>
                            <input type="file" accept="video/*" style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} onChange={handleVideoChange} />
                            <Typography sx={{ fontSize: '12px' }}>Upload Video</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {typeof form.video_file !== 'string' && (
                              <Typography sx={{ fontSize: '12px' }} title={form.video_file_name}>
                                {form.video_file_name}
                              </Typography>
                            )}
                            {typeof form.video_file === 'string' && (
                              <Button
                                variant="outlined"
                                size="small"
                                component="a"
                                href={form.video_file}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ textTransform: 'none', fontSize: '12px', lineHeight: 1, height: "32px" }}
                              >
                                View video
                              </Button>
                            )}
                            <Button color="error" onClick={handleRemoveVideo} sx={{ textTransform: 'none' }}>Remove</Button>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ minWidth: 220, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {!form.json_file ? (
                          <Box sx={{ position: 'relative', border: '1px dashed', borderColor: 'secondary.border', borderRadius: '6px', px: 1, py: 0.5 }}>
                            <input type="file" accept=".json,application/json" style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} onChange={handleJsonChange} />
                            <Typography sx={{ fontSize: '12px' }}>Upload JSON</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '12px' }} title={form.json_file_name}>
                              {form.json_file_name}
                            </Typography>
                            <Button color="error" onClick={handleRemoveJson} sx={{ textTransform: 'none' }}>Remove</Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                <Typography sx={{ fontSize: "14PX", fontWeight: 600 }}>Banners</Typography>
                <Button variant="outlined" onClick={addBanner} sx={{ textTransform: "none" }}>Add Banner</Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {form.banners.map((banner, idx) => (
                  <Box key={idx} sx={{ p: 2, border: "1px solid", borderColor: "secondary.border", borderRadius: 1, backgroundColor: "background.light", display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ fontWeight: 600 }}>Banner #{idx + 1}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="body2">Active</Typography>
                        <Switch checked={banner.is_active} name="is_active" onChange={(e) => updateBannerSwitch(idx, e)} />
                        {form.banners.length > 1 && (
                          <Button color="error" onClick={() => removeBanner(idx)} sx={{ textTransform: "none" }}>Remove</Button>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                        <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Heading</Typography>
                        <InputBase name="heading" value={banner.heading} onChange={(e) => updateBannerField(idx, e)} placeholder="Enter heading"
                          sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                        <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Background Color</Typography>
                        <InputBase name="background_color" value={banner.background_color} onChange={(e) => updateBannerField(idx, e)} placeholder="#FFFFFF or css color"
                          sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                      </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: { xs: "100%", md: "48%" } }}>
                      <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Content Color</Typography>
                      <InputBase name="content_color" value={banner.content_color || ''} onChange={(e) => updateBannerField(idx, e)} placeholder="#000000 or css color"
                        sx={{ backgroundColor: "white", height: "32px", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "5px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                    </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                        <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Description</Typography>
                        <InputBase name="description" value={banner.description} onChange={(e) => updateBannerField(idx, e)} placeholder="Enter description" multiline rows={3}
                          sx={{ backgroundColor: "white", width: "100%", borderRadius: "2px", border: "1px solid", borderColor: "secondary.border", padding: "8px 12px", fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                        <Typography sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}>Images</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          {(banner.imagePreviews || []).map((preview, imgIdx) => (
                            <Box key={imgIdx} sx={{ width: '104px', height: '104px', border: '1px solid', borderColor: 'secondary.border', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', cursor: 'pointer', backgroundColor: 'background.upload', overflow: 'hidden', '&:hover .overlay': { display: 'block', }, '&:hover .delete-button': { display: 'flex', }, }}>
                              <img src={preview} alt={`Uploaded ${imgIdx}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s' }} />
                              <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px' }} />
                              <Tooltip title="Delete" arrow>
                                <IconButton className="delete-button" onClick={() => handleRemoveBannerImage(idx, imgIdx)} sx={{ position: 'absolute', top: '50%', left: '50%', display: 'none', transform: 'translate(-50%, -50%)' }}>
                                  <DeleteOutlineIcon sx={{ color: 'white' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}

                          <Box sx={{ width: '104px', height: '104px', border: '1px solid', borderColor: 'secondary.border', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', cursor: 'pointer', backgroundColor: 'background.upload' }}>
                            <input type="file" accept="image/*" style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0 }} onChange={(e) => handleBannerFileChange(idx, e)} />
                            <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <AddIcon fontSize="large" />
                              <Typography sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '22px' }}>Upload</Typography>
                            </label>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, padding: "12px 24px", borderTop: "1px solid", borderColor: "secondary.border" ,height:"60px"}}>
              <Button variant="outlined" onClick={handleClose} sx={{ textTransform: "none" }}>Cancel</Button>
              <Button variant="contained" onClick={saveLaunchScreen} disabled={saving} sx={{ textTransform: "none" }}>{saving ? "Saving..." : "Save"}</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}


